import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { Scene, sortScenes } from '@lib/data/story';
import { useDebouncedValue } from '@lib/hooks/debounce';
import { useAutoScrollToActive } from '@lib/hooks/useAutoScrollToActive';
import { Segments } from '@lib/transcript/types';
import { getColorGoodAgainstWhiteText } from '@lib/utils/colorUtils';
import { formatClockLabel } from '@lib/utils/units';
import { BeginEndTimestamp } from '@stores/MomentMerge/MomentMergeStore';
import { trackingStore } from '@stores/Tracking';
import { videoPlayerStore, VideoPlayerStore } from '@stores/VideoPlayer';
import { observer } from 'mobx-react';
import { Fragment, useMemo } from 'react';
import { VideoPlayerRef } from '../VideoPlayer/VideoPlayer';
import { Sentence } from './components/Sentence/Sentence';
import css from './TranscriptLoader.module.scss';

export const TranscriptLoader = observer(
  ({
    videoPlayerRef,
    transcript,
    error,
    scenes,
    store = videoPlayerStore,
  }: {
    transcript: Segments | null | undefined;
    error?: Error;
    /** Scenes to break the transcript into chapters. Omit and it renders as one flat run. */
    scenes?: Scene[];
    videoPlayerRef: React.RefObject<VideoPlayerRef | null>;
    store?: VideoPlayerStore;
  }) => {
    const { activeRef: activeSentenceRef, scrollRef } = useAutoScrollToActive<HTMLDivElement>();
    const debouncedCurrentTime = useDebouncedValue(store.currentTime, 50);

    // Read the observables in the render body so the `observer` tracks them, then feed the
    // values to the memos below rather than reading the store inside them.
    const mode = store.mode;
    const moment = store.moment;

    const segments = useMemo(() => {
      const all = transcript?.segments ?? [];
      return mode === 'moment' && moment
        ? all.filter((s) => s.start < moment.end && s.end > moment.start)
        : all;
    }, [transcript, mode, moment]);

    /**
     * Which segment index each scene heading goes above, so the transcript carries the same
     * structure the Scenes panel shows: an hour-long transcript is otherwise one undivided run
     * of lines with nothing to navigate by.
     *
     * A pointer walk rather than a lookup per segment. Both lists are sorted by time, and this
     * re-runs whenever the transcript or the scenes change — a `find` per segment would be
     * thousands of comparisons on every pass.
     */
    const headingByIndex = useMemo(() => {
      const headings = new Map<number, Scene>();
      const ordered = sortScenes(scenes ?? []);
      if (ordered.length === 0) {
        return headings;
      }
      let cursor = 0;
      let lastSceneId: number | null = null;
      for (let i = 0; i < segments.length; i += 1) {
        const start = segments[i].start;
        while (cursor + 1 < ordered.length && (ordered[cursor + 1].start ?? 0) <= start) {
          cursor += 1;
        }
        const scene = ordered[cursor];
        // Segments before the first scene begins get no heading rather than being filed under
        // a scene that has not started.
        if ((scene.start ?? 0) <= start && scene.scene_id !== lastSceneId) {
          headings.set(i, scene);
          lastSceneId = scene.scene_id;
        }
      }
      return headings;
    }, [segments, scenes]);

    if (error) {
      return <span className={css.placeholder}>Failed to load transcript</span>;
    }

    if (transcript === null) {
      return <span className={css.placeholder}>No transcript available</span>;
    }

    if (!transcript) {
      return <span className={css.placeholder}>Loading...</span>;
    }

    if (segments.length === 0) {
      return <span className={css.rightPanelContent}>No transcript available</span>;
    }

    const speakerSet = new Set(segments.map((s) => s.speaker));
    const speakerColorMap = new Map<string | null, string>();
    Array.from(speakerSet).forEach((s, index) => {
      speakerColorMap.set(s, getColorGoodAgainstWhiteText(index, speakerSet.size, segments.length));
    });

    return (
      <div className={css.rightPanelContent}>
        <GradientScrollDiv ref={scrollRef}>
          {segments.map((s, index) => {
            const isActive =
              s.start - 0.1 <= debouncedCurrentTime && s.end + 0.1 >= debouncedCurrentTime;
            const sentenceTimestamps = new BeginEndTimestamp(s.start, s.end);
            const momentTimestamps = moment
              ? new BeginEndTimestamp(moment.start, moment.end)
              : null;
            const heading = headingByIndex.get(index);
            return (
              <Fragment key={`${s.start}-${index}`}>
                {heading && (
                  <div className={css.sceneHeading}>
                    <span className={css.sceneHeadingTime}>
                      {formatClockLabel(heading.start ?? 0)}
                    </span>
                    <span className={css.sceneHeadingTitle}>
                      {heading.title ?? `Scene ${heading.scene_id}`}
                    </span>
                  </div>
                )}
                <Sentence
                  isActive={isActive}
                  sentenceTimestamps={sentenceTimestamps}
                  momentTimestamps={momentTimestamps}
                  speaker={s.displaySpeaker || s.speaker || 'Unknown'}
                  speakerColor={speakerColorMap.get(s.speaker) || 'black'}
                  text={s.text}
                  onClick={() => {
                    trackingStore.transcriptSentenceClicked({ timestamp: s.start });
                    videoPlayerRef.current?.scrubTo(s.start);
                  }}
                  activeSentenceRef={activeSentenceRef}
                  mode={mode}
                  store={store}
                />
              </Fragment>
            );
          })}
        </GradientScrollDiv>
      </div>
    );
  },
);
